import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  BaseDialog,
  Button,
  DownloadCloud01Icon,
  Stack,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Box,
  Alert,
} from '@metarouter-private/mr-mui';
import yaml from 'js-yaml';

import { useQueryAllAnalyticsFiles } from '@ion/ion-experimental';
import useFeatureEnabled from 'app/launch-darkly/use-feature-enabled';
import { FEATURE_FLAGS } from 'app/launch-darkly/featureFlags';
import { useTrackWithUser } from '@ion/api';
import useQueryConsentCategories from '@ion/api/consent-categories/useQueryConsentCategories';
import FileSelectionList, { ConfirmFileList } from '../shared/FileSelectionList';
import generateBulkDownloadYaml from './generateBulkDownloadYaml';

const STEPS = ['Select Files', 'Confirm'];
const MODAL_STEP = {
  SELECT: 'SELECT',
  CONFIRM: 'CONFIRM',
};

export default function BulkDownloadModal({ open, onClose }) {
  const { data: analyticsFiles, loading } = useQueryAllAnalyticsFiles({
    skip: !open,
    fetchPolicy: 'network-only',
  });
  const { builderServerQueueTasksEnabled } = useFeatureEnabled(FEATURE_FLAGS.builderServerQueueTasks);
  const {
    data: consentCategories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useQueryConsentCategories({ skip: !open, fetchPolicy: 'network-only' });
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [trackBulkDownload] = useTrackWithUser('bulk_analytics_files_downloaded');

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [step, setStep] = useState(MODAL_STEP.SELECT);

  const selectedFiles = analyticsFiles?.filter(file => selectedIds.has(file.id)) ?? [];
  const needsCategories = selectedFiles.some(file => Boolean(file.settings?.matchIDConsentCode));

  const reset = useCallback(() => {
    setSelectedIds(new Set());
    setStep(MODAL_STEP.SELECT);
    setDownloadError(null);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const goToConfirm = () => setStep(MODAL_STEP.CONFIRM);
  const goToSelect = () => setStep(MODAL_STEP.SELECT);

  const handleDownload = () => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const bulkYaml = generateBulkDownloadYaml(selectedFiles, {
        queueTasks: builderServerQueueTasksEnabled,
        consentCategories,
      });
      const yamlContent = yaml.dump(bulkYaml);

      const blob = new Blob([yamlContent], { type: 'text/yaml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.download = `analytics-files-bulk-download-${timestamp}.yaml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      trackBulkDownload();
      onClose();
    } catch (error) {
      setDownloadError(error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const activeStep = step === MODAL_STEP.SELECT ? 0 : 1;

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (step === MODAL_STEP.SELECT) {
      return (
        <FileSelectionList files={analyticsFiles ?? []} selectedIds={selectedIds} onSelectionChange={setSelectedIds} />
      );
    }

    const description = `Download ${selectedFiles.length} analytics file${
      selectedFiles.length !== 1 ? 's' : ''
    } as a single YAML document. This file can be edited and re-uploaded using the Bulk Upload feature.`;
    return <ConfirmFileList files={selectedFiles} description={description} />;
  };

  const renderActions = () => {
    if (step === MODAL_STEP.SELECT) {
      return (
        <>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={goToConfirm}
            disabled={selectedIds.size === 0 || loading}
            data-testid="bulk-download-config-continue-btn"
          >
            Continue
          </Button>
        </>
      );
    }

    return (
      <>
        <Button variant="text" onClick={goToSelect} disabled={isDownloading}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={isDownloading || (needsCategories && (categoriesLoading || Boolean(categoriesError)))}
          data-test-id="bulk-download-button"
        >
          {isDownloading ? 'Downloading...' : 'Download'}
        </Button>
      </>
    );
  };

  return (
    <BaseDialog
      DialogProps={{
        open,
        maxWidth: 'sm',
        fullWidth: true,
        onClose,
        'data-test-id': 'bulk-download-config-modal',
      }}
      title="Bulk Download Config"
      TitleIcon={DownloadCloud01Icon}
      content={
        <Stack spacing={3}>
          <Stepper activeStep={activeStep} sx={{ pt: 1 }}>
            {STEPS.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {(downloadError || (needsCategories && categoriesError)) && (
            <Alert severity="error">
              {downloadError || 'Consent categories could not be loaded. Reopen this dialog to retry.'}
            </Alert>
          )}
          {renderContent()}
        </Stack>
      }
      actions={renderActions()}
    />
  );
}

BulkDownloadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
